import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { GoogleMapsProvider, loadGoogleMaps } from '~/components/AddressAutocomplete';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreStatic } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { prevBusinessIdKey, useSwitchBusiness } from '~/hooks/useSwitchBusiness';
import { useHomePage } from '~/pages/HomePage/HomePageProvider';
import { clearPersistedOnboardingProgress, deriveVobMode, readPersistedCurrentStep } from './hooks/useOnboardingFlow';
import { VendorOnboarding } from './VendorOnboarding';
export default function VendorOnboardingPage() {
    var _ref;
    const history = useHistory();
    const { SP_ONBOARDING_PHASE_1 = false } = useFeatureFlags();
    const { localStorage, browser: { changeLocation } } = useIoContext();
    const { switchBusiness } = useSwitchBusiness();
    const { setHidden } = useHomePage();
    const { getState } = useStoreStatic();
    const userInfo = useStore((state)=>state.userInfo);
    const isClient = userInfo === null || userInfo === void 0 ? void 0 : userInfo.isCustomer();
    const workspaces = (_ref = userInfo === null || userInfo === void 0 ? void 0 : userInfo.workspaces()) !== null && _ref !== void 0 ? _ref : [];
    const hasIdentity = !!(userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasIdentity());
    const hasCompletedVendorWorkspace = workspaces.some((w)=>w.isVendor() && w.isCompletelySetup());
    const currentBusinessId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId();
    const currentWorkspaceIsCompletedVendor = currentBusinessId != null && workspaces.some((ws)=>ws.isVendor() && ws.isCompletelySetup() && ws.id() === currentBusinessId);
    const handleComplete = useCallback(()=>{
        clearPersistedOnboardingProgress();
        history.replace(routes.vendorDashboard);
    }, [
        history
    ]);
    const switchWithHidden = useCallback(async (businessId)=>{
        setHidden(true);
        try {
            await switchBusiness(businessId);
        } finally{
            setHidden(false);
        }
    }, [
        setHidden,
        switchBusiness
    ]);
    const handleClose = useCallback(async ()=>{
        var _ref;
        var _getState_claims, _userWorkspaces_find;
        clearPersistedOnboardingProgress();
        const currentUserInfo = getState().userInfo;
        const currentBusinessId = currentUserInfo === null || currentUserInfo === void 0 ? void 0 : currentUserInfo.businessId();
        const cookieBusinessId = (_getState_claims = getState().claims) === null || _getState_claims === void 0 ? void 0 : _getState_claims.businessId;
        const userWorkspaces = (_ref = currentUserInfo === null || currentUserInfo === void 0 ? void 0 : currentUserInfo.workspaces()) !== null && _ref !== void 0 ? _ref : [];
        const cookieIsOnCompletedWorkspace = cookieBusinessId != null && userWorkspaces.some((ws)=>ws.id() === cookieBusinessId && ws.isCompletelySetup());
        if (cookieIsOnCompletedWorkspace) {
            const worksAtMissing = (currentUserInfo === null || currentUserInfo === void 0 ? void 0 : currentUserInfo.business()) == null;
            if (worksAtMissing) {
                changeLocation(routes.home);
                return;
            }
            history.replace(routes.home);
            return;
        }
        const stashedPrevBusinessId = localStorage.getItem(prevBusinessIdKey);
        const stashedIsAccessible = stashedPrevBusinessId != null && stashedPrevBusinessId !== currentBusinessId && userWorkspaces.some((ws)=>ws.id() === stashedPrevBusinessId);
        const targetBusinessId = stashedIsAccessible ? stashedPrevBusinessId : (_userWorkspaces_find = userWorkspaces.find((ws)=>ws.isCompletelySetup() && ws.id() !== currentBusinessId)) === null || _userWorkspaces_find === void 0 ? void 0 : _userWorkspaces_find.id();
        if (!targetBusinessId) return;
        await switchWithHidden(targetBusinessId);
    }, [
        changeLocation,
        getState,
        history,
        localStorage,
        switchWithHidden
    ]);
    useEffect(()=>{
        if (!SP_ONBOARDING_PHASE_1) return undefined;
        window.history.pushState(null, '', window.location.href);
        const handleBrowserBack = ()=>{
            var _ref;
            const u = getState().userInfo;
            const currentId = u === null || u === void 0 ? void 0 : u.businessId();
            const onCompletedVendor = currentId != null && ((_ref = u === null || u === void 0 ? void 0 : u.workspaces()) !== null && _ref !== void 0 ? _ref : []).some((w)=>w.isVendor() && w.isCompletelySetup() && w.id() === currentId);
            const userIsInvitee = !(u === null || u === void 0 ? void 0 : u.hasIdentity()) && onCompletedVendor;
            if (userIsInvitee) {
                window.history.pushState(null, '', window.location.href);
                return;
            }
            void handleClose();
        };
        window.addEventListener('popstate', handleBrowserBack);
        return ()=>window.removeEventListener('popstate', handleBrowserBack);
    }, [
        SP_ONBOARDING_PHASE_1,
        getState,
        handleClose
    ]);
    if (!SP_ONBOARDING_PHASE_1) return /*#__PURE__*/ _jsx(Redirect, {
        to: routes.home
    });
    const arrivedViaButton = localStorage.getItem(prevBusinessIdKey) != null;
    if (isClient && !arrivedViaButton) {
        return /*#__PURE__*/ _jsx(Redirect, {
            to: routes.home
        });
    }
    const hasPersistedProgress = readPersistedCurrentStep() != null;
    const claims = getState().claims;
    const claimsRequireIdentity = (claims === null || claims === void 0 ? void 0 : claims.setIdentityOnly) === true;
    if (currentWorkspaceIsCompletedVendor && !hasPersistedProgress && !claimsRequireIdentity) {
        return /*#__PURE__*/ _jsx(Redirect, {
            to: routes.home
        });
    }
    const vobMode = deriveVobMode({
        sp_onboarding_phase_1: !!SP_ONBOARDING_PHASE_1,
        isClient: !!isClient,
        hasCustomerWorkspace: workspaces.some((w)=>w.isCustomer()),
        hasCompletedVendorWorkspace,
        currentWorkspaceIsCompletedVendor,
        hasIdentity
    });
    return /*#__PURE__*/ _jsx(GoogleMapsProvider, {
        loadGoogleMaps: loadGoogleMaps,
        children: /*#__PURE__*/ _jsx(VendorOnboarding, {
            mode: vobMode,
            onComplete: handleComplete,
            onClose: handleClose
        })
    });
}
